// A standing indicator that outbound work has permanently failed.
//
// WHY THIS EXISTS. On 2026-09-11 an RFQ's push to the CRM died: the contact's
// email was the literal string "<unknown>", GHL answered 422, and a 422 is not
// retryable so the event went to `dead` on its first attempt. The lead sat in
// the app and never reached the CRM, and nobody found out until someone went
// looking in the database.
//
// The machinery to notice already existed and none of it reached the operator.
// The drain captures a Sentry message, which is a developer channel. There is a
// read-only endpoint at /api/internal/outbox/dead, which nothing called. So a
// silent failure stayed silent — the exact class of problem this app's outbox
// was built to make visible.
//
// DELIBERATELY QUIET WHEN THERE IS NOTHING TO SAY. It renders nothing at zero,
// so it cannot become the banner everyone learns to scroll past. Owner and
// admin only, matching the endpoint's own role gate.
//
// ONLY THE LAST 7 DAYS. It used to count every dead event since May — 431 on
// 2026-10-21, 290 of them CRM timeline notes from the summer — so it never
// reached zero, never changed, and the one new failure that mattered looked
// exactly like the day before. Now it counts events that died in the last
// seven days and are still dead (a replayed event leaves `dead`), so the
// banner appearing at all means something just broke. The full history stays
// at /api/internal/outbox/dead without `days`.

import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { api } from '@/lib/api';

const DEAD_WINDOW_DAYS = 7;

interface DeadLetterSummary {
  total: number;
  categories: Array<{ signature: string; count: number; eventTypes: string[] }>;
}

export function DeadLetterBanner() {
  const { getToken } = useAuth();

  const { data } = useQuery({
    queryKey: ['outbox-dead', DEAD_WINDOW_DAYS],
    queryFn: () =>
      api<DeadLetterSummary>(`/api/internal/outbox/dead?days=${DEAD_WINDOW_DAYS}`, { tokenGetter: () => getToken() }),
    // Five minutes: a dead letter is already permanent, so there is nothing to
    // gain from polling hard, and this fires for every operator on every page.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    // A 403 for a non-elevated role is the expected answer, not a fault worth
    // retrying or logging.
    retry: false,
  });

  if (!data || data.total === 0) return null;

  const top = data.categories[0];

  return (
    <div className="flex items-start gap-2 border-b border-border bg-warning-muted px-4 lg:px-6 py-2 text-xs">
      <AlertTriangle className="w-4 h-4 text-warning shrink-0 mt-0.5" />
      <div className="min-w-0">
        <span className="font-medium text-warning">
          {data.total} outbound {data.total === 1 ? 'event' : 'events'} permanently failed in the last {DEAD_WINDOW_DAYS} days
        </span>
        {top && (
          <span className="text-foreground-muted">
            {' '}— most common: {top.eventTypes.join(', ')} ({top.count}×){' '}
            <span className="font-mono">{top.signature.slice(0, 70)}</span>
          </span>
        )}
        <div className="text-[11px] text-foreground-muted mt-0.5">
          These will not retry on their own. Fix the underlying record, then replay the event.
        </div>
      </div>
    </div>
  );
}
